import React, {PureComponent, Fragment} from 'react';
import styled from 'styled-components';
import DayPicker from 'react-day-picker';
import {v4 as uuid} from 'uuid';

import Input from '../Input';
import Card from '../Card';
import Popover from '../Popover';
import Button from '../Button';

import 'react-day-picker/lib/style.css';

class DateTimeInput extends PureComponent {
  static defaultProps = {
    utc: false,
    dateOnly: false,
    timeOnly: false
  };

  id = uuid();
  state = {
    showDateTime: false
  };

  openDateTime = () => {
    this.setState({showDateTime: true});
  }

  hideDateTime = () => {
    this.setState({showDateTime: false});
  }

  handleOk = () => {
    const {date, onChange} = this.props;
    
    if (date) {
      onChange(date);
    }

    this.hideDateTime();
  }

  handleClear = () => {
    this.props.onChange(null);
    this.hideDateTime();
  }

  handleSelectedDay = nextDate => {
    const {date, utc, onChange} = this.props;
    const hour = date ? (utc ? date.getUTCHours() : date.getHours()) : 0;
    const min = date ? (utc ? date.getUTCMinutes() : date.getMinutes()) : 0;

    if (utc) {
      onChange(new Date(Date.UTC(nextDate.getUTCFullYear(), nextDate.getUTCMonth(), nextDate.getUTCDate(), hour, min)));
    }
    else {
      onChange(new Date(nextDate.getFullYear(), nextDate.getMonth(), nextDate.getDate(), hour, min));
    }
  }

  handleChangeHour = e => {
    const {utc, onChange} = this.props;
    const date = this.props.date || this.getNullDate()
    const hour = parseInt(e.target.value) + (12 * getAmOrPm(this.getNormalizedDate()));

    if (utc) {
      onChange(new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), hour, date.getUTCMinutes())));
    }
    else {
      onChange(new Date(date.getFullYear(), date.getMonth(), date.getDate(), hour, date.getMinutes()));
    }
  }

  handleChangeMinute = e => {
    const {utc, onChange} = this.props;
    const date = this.props.date || this.getNullDate()
    const minutes = parseInt(e.target.value);

    if (utc) {
      onChange(new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), date.getUTCHours(), minutes)));
    }
    else {
      onChange(new Date(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), minutes));
    }
  }

  handleChangeAmPm = e => {
    const {utc, onChange} = this.props;
    const date = this.getNormalizedDate();
    let hour = parseInt(e.target.value) === 0 ? date.getHours() - 12 : date.getHours() + 12;

    if (hour > 23) {
      hour = 0;
    }

    if (utc) {
      onChange(new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), hour, date.getUTCMinutes())));
    }
    else {
      onChange(new Date(date.getFullYear(), date.getMonth(), date.getDate(), hour, date.getMinutes()));
    }
  }

  getNormalizedDate = () => {
    const {date, utc} = this.props;

    return (date && utc)
      ? new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), date.getUTCHours(),
        date.getUTCMinutes(), date.getUTCSeconds())
      : date;
  }

  getNullDate = () => {
    const {utc} = this.props;
    const date = new Date();
    if (utc) {
      date.setUTCHours(0);
      date.setUTCMinutes(0);
      return date;
    }
    else {
      date.setHours(0);
      date.setMinutes(0);
      return date;
    }
  }

  getInputValue = () => {
    const {dateOnly, timeOnly} = this.props;
    const date = this.getNormalizedDate();

    if (!date) {
      return '';
    }
    
    if (dateOnly) {
      return date.toLocaleDateString();
    }

    if (timeOnly) {
      return date.toLocaleTimeString();
    }

    return date.toLocaleString();
  }

  render() {
    const {dateOnly, timeOnly, utc, ...inputProps} = this.props;
    const {showDateTime} = this.state;
    const date = this.getNormalizedDate();

    return (
      <Fragment>
        <Input
          {...inputProps}
          readOnly
          id={this.id}
          type="text"
          value={this.getInputValue()}
          onFocus={this.openDateTime}
        />
        <Popover open={showDateTime} relativeTo={this.id} onClose={this.hideDateTime}>
          <Card>
            {!timeOnly && (
              <Calendar>
                <DayPicker onDayClick={this.handleSelectedDay} selectedDays={date} />
              </Calendar>
            )}
            {!dateOnly && (
              <TimeOptions>
                <Input
                  disabled={!date && !timeOnly}
                  type="select"
                  value={getHours(date, utc)}
                  onChange={this.handleChangeHour}>
                  {new Array(12).fill(null).map((_, i) => (
                    <option key={i} value={i}>{i === 0 ? 12 : i}</option>
                  ))}
                </Input>
                <span>:</span>
                <Input
                  disabled={!date && !timeOnly}
                  type="select"
                  value={getMinutes(date, utc)}
                  onChange={this.handleChangeMinute}>
                  {new Array(60).fill(null).map((_, i) => (
                    <option key={i} value={i}>{zeroPadding(i)}</option>
                  ))}
                </Input>
                <Input
                  disabled={!date && !timeOnly}
                  type="select"
                  value={getAmOrPm(date, utc)}
                  onChange={this.handleChangeAmPm}>
                  <option value={0}>AM</option>
                  <option value={1}>PM</option>
                </Input>
              </TimeOptions>
            )}
            <Actions>
              <Button block secondary onClick={this.handleClear}>Clear</Button>
              <Button block primary onClick={this.handleOk} disabled={!this.props.date}>OK</Button>
            </Actions>
          </Card>
        </Popover>
      </Fragment>
    );
  }
}

function getHours(date) {
  if (!date) {
    return 0;
  }

  const hours = date.getHours();
  return hours < 12 ? hours : hours - 12;
}

function getMinutes(date) {
  if (!date) {
    return 0;
  }

  return date.getMinutes();
}

function getAmOrPm(date) {
  if (!date) {
    return 0;
  }

  return date.getHours() < 12 ? 0 : 1;
}

function zeroPadding(num) {
  if (num < 10) {
    return '0' + num;
  }

  return num;
}

const Calendar = styled.div`
  border-bottom: 0.0625rem solid #E2E4EA;
`;

const TimeOptions = styled.div`
  display: flex;
  border-bottom: 0.0625rem solid #E2E4EA;
  padding: 1rem;
  align-items: center;

  > * {
    flex: 1;
    padding-right: 0.5rem;

    :last-child {
      padding-right: 0;
    }
  }

  > span {
    flex: 0;
  }
`;

const Actions = styled.div`
  padding: 1rem;
  display: flex;
  gap: 1rem;
`;

export default DateTimeInput;